import { inject, Injectable } from '@angular/core';
import { ComplexArticleBlueprintDTO, ComplexArticleBlueprintError, ComplexArticleRule, ParsedArticleNumberSchema } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { SaveAsCompositeMode } from '@project/components/project/project-save/save-project.service';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { Log } from '@shared/debug/log';
import { NumberUtils } from '@shared/util/number-utils';
import { evaluate } from 'mathjs';
import { firstValueFrom, lastValueFrom } from 'rxjs';

@Injectable({providedIn: 'root'})
export class ComplexArticleBlueprintFactory {

  private readonly projectArticleManager = inject(ProjectArticleManager);
  private readonly articleManager = inject(ArticleManager);
  private readonly articleCreationManagerFactory = inject(ArticleCreationManagerFactory);
  private readonly mountingRailHelper = inject(MountingRailHelper);

  private readonly ruleAddArticles = new ComplexArticleRule('addArticles', {
      articleNoOrIds: {
        type: 'STRING',
        required: true,
      },
      amount: {
        type: 'STRING',
        required: true,
        default: 'num',
      }
    },

    async (params, blueprint, parsedArticleNumberSchema) => {
      const articleNoOrIds = params.getValue('articleNoOrIds', 'string');
      const amountExpression = params.getValue('amount', 'string');

      let amount = 1;
      try {
        amount = this.parseArticleNumberExpression(amountExpression, parsedArticleNumberSchema);
      } catch (e) {
        return [{message: `Error evaluating 'amountExpression': ${e instanceof Error ? e.message : String(e)}`}];
      }

      if(!NumberUtils.isNumber(amount) || amount < 1) {
        return [{message: `Expression 'amountExpression' cannot be resolved to a positive integer`}];
      }

      await this.addArticles(articleNoOrIds, amount);
    });


  private readonly ruleAddAccessory = new ComplexArticleRule('addAccessory', {
      articleNoOrId: {
        type: 'STRING',
        required: true,
      },
      articlePositions: {
        type: 'STRING',
        isArray: true,
        required: true,
      },
      metaElementNames: {
        type: 'STRING',
        isArray: true,
        required: true,
      }
    },

    async (params, blueprint, parsedArticleNumberSchema) => {
      const articleNoOrId = params.getValue('articleNoOrId', 'string');
      let parsedPositions: number[] = [];

      try {
        parsedPositions = params.getValues('articlePositions', 'string')
          .map(positionExpression => this.parseArticleNumberExpression(positionExpression, parsedArticleNumberSchema));
      } catch (e) {
          return [{message: `Error evaluating 'articlePositions': ${e instanceof Error ? e.message : String(e)}`}];
      }

      const metaElementNames = params.getValues('metaElementNames', 'string');
      const accessoryArticle = await lastValueFrom(this.articleManager.getArticleByIdOrArticleNumberAndVersion(articleNoOrId, -1));

      if (!Log.assertExists(accessoryArticle)) {
        return [{message: `article ${articleNoOrId} not found`}];
      }

      const articlesOnRail: ArticleContainer[] = this.mountingRailHelper.getRailArticleContainers();

      const containers = parsedPositions.map((index) => articlesOnRail[index]);
      const connections: Connection[] = [];

      for (let i = 0; i < containers.length; i++) {
        if(!containers[i]) {
          return [{message: `There is no article at position ${parsedPositions[i]}`}];
        }
        const connectionToAdd = containers[i].connections.find((connection) => connection.metaElementVO.name === metaElementNames[i]);
        if (!connectionToAdd) {
          return [{message: `Connection ${metaElementNames[i]} not found on article at position ${parsedPositions[i]}`}];
        }
        connections.push(connectionToAdd);
      }

      return await this.addAccessoryToConnections(accessoryArticle, connections);
    });

  private async addAccessoryToConnections(accessoryArticle: ArticleVO, connections: Connection[]): Promise<ComplexArticleBlueprintError[]> {
    const articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    articleCreationManager.showFaultMessage = false;
    await articleCreationManager.createArticlesByAccessoryArticleAndAcceptingCubes(accessoryArticle, connections, true);
    return articleCreationManager.getCreatedArticleContainers().length === 0 ?
      [{message: `Could not attach accessory ${accessoryArticle.articleNo} to the specified connection(s)`}] :
      [];
  }

  public allRules: ComplexArticleRule[] = [
    this.ruleAddArticles,
    this.ruleAddAccessory,
  ];

  private parseArticleNumberExpression(expression: string, parsedArticleNumberSchema: ParsedArticleNumberSchema): number {
    const amountExpression = expression.replaceAll('num', parsedArticleNumberSchema.articlesAmount.toString());
    return evaluate(amountExpression);
  }

  private async addArticles(articleNoOrIds: string, amount: number): Promise<ComplexArticleBlueprintError[]> {
    const articleNoOrIdList = articleNoOrIds.split(';').map((str) => str.trim());

    for (let i = 0; i < amount; i++) {
      for (const articleNoOrId of articleNoOrIdList) {
        const article = await firstValueFrom(this.articleManager.getArticleByIdOrArticleNumberAndVersion(articleNoOrId, -1));
        if (!article) {
          return [{message: `Article ${articleNoOrId} not found`}];
        }
        await this.projectArticleManager.addArticles(article, 1);
      }
    }
    return [];
  }

  public findRule(ruleName: string): ComplexArticleRule | undefined {
    return this.allRules.find((rule) => rule.name === ruleName);
  }

  public newBlueprint(name: string): ComplexArticleBlueprintDTO {
    return {
      name,
      rules: [],
      articleNumberSchema: {
        articleNumber: '',
        from: 1,
        to: 1,
        additional: [],
        excluded: [],
      },
      description: '',
      flags: {
        isIndividualPartsModifiable: false,
        isIndividualPartsSelectable: false,
        isIndividualPartsStickTogether: false,
        isInitialRotationRequired: false,
      },
      projectGroupId: -1,
      saveMode: SaveAsCompositeMode.ARTICLE,
      articleTypeId: -1,
    }
  }
}
